import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { HIGHLIGHTS, SWATCHES } from './model';

export function ToolButton({ children, label, onClick, active, disabled, shortcut }: {
  children: React.ReactNode; label: string; onClick: () => void; active?: boolean; disabled?: boolean; shortcut?: string;
}) {
  return (
    <button
      type="button"
      title={shortcut ? `${label} (${shortcut})` : label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={event => event.preventDefault()}
      onClick={onClick}
      className={`p-1.5 rounded transition-colors ${active ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100 text-gray-700'} disabled:opacity-30 disabled:hover:bg-transparent`}
    >
      {React.isValidElement(children) ? React.cloneElement(children as any, { size: 16 }) : children}
    </button>
  );
}

export const Separator = () => <span className="h-6 w-px bg-gray-200 mx-1" aria-hidden="true" />;

export function Menu({ label, icon, children, width = 'w-56', ariaLabel }: {
  label?: React.ReactNode; icon?: React.ReactNode; children: (close: () => void) => React.ReactNode; width?: string; ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const holder = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => { if (!holder.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', escape); };
  }, [open]);
  return (
    <div className="relative" ref={holder}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onMouseDown={event => event.preventDefault()}
        onClick={() => setOpen(value => !value)}
        className="flex items-center gap-1 px-2 py-1.5 rounded text-sm text-gray-700 hover:bg-gray-100"
      >
        {icon && React.isValidElement(icon) ? React.cloneElement(icon as any, { size: 16 }) : icon}
        {label && <span className="max-w-[140px] truncate">{label}</span>}
        <ChevronDown size={13} className="opacity-60" />
      </button>
      {open && (
        <div role="menu" className={`absolute z-40 mt-1 ${width} bg-white border border-gray-200 rounded-lg shadow-lg p-1 max-h-80 overflow-auto`}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

export function MenuItem({ children, onClick, active, description }: { children: React.ReactNode; onClick: () => void; active?: boolean; description?: string }) {
  return (
    <button
      type="button"
      role="menuitem"
      onMouseDown={event => event.preventDefault()}
      onClick={onClick}
      className={`w-full text-left px-2 py-1.5 rounded text-sm flex flex-col ${active ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-100'}`}
    >
      <span>{children}</span>
      {description && <span className="text-[11px] text-gray-500">{description}</span>}
    </button>
  );
}

export function ColorMenu({ ariaLabel, icon, value, onPick, swatches = SWATCHES, allowClear = true, clearLabel = 'Ohne Farbe' }: {
  ariaLabel: string; icon: React.ReactNode; value?: string; onPick: (color?: string) => void; swatches?: string[]; allowClear?: boolean; clearLabel?: string;
}) {
  return (
    <Menu ariaLabel={ariaLabel} icon={icon} width="w-56">
      {close => (
        <div className="p-1">
          <div className="grid grid-cols-7 gap-1">
            {swatches.map(color => (
              <button
                key={color}
                type="button"
                aria-label={`${ariaLabel}: ${color}`}
                onMouseDown={event => event.preventDefault()}
                onClick={() => { onPick(color); close(); }}
                className={`h-5 w-5 rounded border ${value === color ? 'ring-2 ring-primary-500' : 'border-gray-300'}`}
                style={{ background: color }}
              />
            ))}
          </div>
          <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
            Eigene Farbe
            <input type="color" aria-label={`${ariaLabel} frei wählen`} value={value || '#111827'} onChange={event => onPick(event.target.value)} className="h-6 w-10 border rounded" />
          </label>
          {allowClear && (
            <button type="button" onMouseDown={event => event.preventDefault()} onClick={() => { onPick(undefined); close(); }} className="mt-1 w-full text-left text-xs px-2 py-1 rounded hover:bg-gray-100">
              {clearLabel}
            </button>
          )}
        </div>
      )}
    </Menu>
  );
}

export const HighlightMenu = (props: Omit<Parameters<typeof ColorMenu>[0], 'swatches'>) => <ColorMenu {...props} swatches={HIGHLIGHTS} clearLabel="Hervorhebung entfernen" />;

export function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block text-xs text-gray-600">
      <span className="block mb-1 font-medium text-gray-700">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-[11px] text-gray-400">{hint}</span>}
    </label>
  );
}

export function NumberField({ label, value, onChange, min = 0, max = 999, step = 1, unit, hint }: {
  label: string; value: number | undefined; onChange: (value: number | undefined) => void; min?: number; max?: number; step?: number; unit?: string; hint?: string;
}) {
  return (
    <Field label={unit ? `${label} (${unit})` : label} hint={hint}>
      <input
        type="number"
        aria-label={label}
        className="w-full border border-gray-300 rounded p-1.5 text-sm"
        value={value ?? ''}
        min={min}
        max={max}
        step={step}
        onChange={event => onChange(event.target.value === '' ? undefined : Math.min(max, Math.max(min, Number(event.target.value))))}
      />
    </Field>
  );
}

export function Toggle({ label, checked, onChange, hint }: { label: string; checked: boolean; onChange: (checked: boolean) => void; hint?: string }) {
  return (
    <label className="flex items-start gap-2 text-xs text-gray-700">
      <input type="checkbox" className="mt-0.5" checked={checked} onChange={event => onChange(event.target.checked)} />
      <span>
        {label}
        {hint && <span className="block text-[11px] text-gray-400">{hint}</span>}
      </span>
    </label>
  );
}

export function SelectField<T extends string | number>({ label, value, options, onChange }: {
  label: string; value: T | undefined; options: { value: T; label: string }[]; onChange: (value: T) => void;
}) {
  return (
    <Field label={label}>
      <select
        aria-label={label}
        className="w-full border border-gray-300 rounded p-1.5 text-sm bg-white"
        value={value as any}
        onChange={event => {
          const raw = event.target.value;
          const match = options.find(option => String(option.value) === raw);
          onChange((match ? match.value : raw) as T);
        }}
      >
        {options.map(option => <option key={String(option.value)} value={String(option.value)}>{option.label}</option>)}
      </select>
    </Field>
  );
}
